import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const handleNavigation = (item: { id: string; path?: string }) => {
    setIsMobileMenuOpen(false);
    
    if (item.path) {
      navigate(item.path);
      return;
    }

    const isHome = location.pathname === '/' || location.pathname === '';
    if (!isHome) {
      navigate({ pathname: '/', hash: item.id });
    } else {
      scrollToSection(item.id);
    }
  };

  const isHomePage = location.pathname === '/' || location.pathname === '';

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      isScrolled || !isHomePage ? 'bg-white/80 dark:bg-dark-surface/80 backdrop-blur-xl shadow-lg border-b border-gray-200/20 dark:border-white/10' : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center">
            <button
              onClick={() => navigate('/')}
              className="cursor-pointer flex items-center"
              aria-label="ホーム"
            >
              <img
                src={`${import.meta.env.BASE_URL}logo-text.png`}
                alt="WEBデザインくまチョップ"
                className="h-12 w-auto object-contain sm:h-14 transition-all duration-300"
                style={{ filter: (isScrolled || !isHomePage) ? 'none' : 'brightness(0) invert(1)' }}
              />
            </button>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {[
              { name: 'ホーム', id: 'hero' },
              { name: 'サービス', id: 'services' },
              { name: 'ポートフォリオ', id: 'portfolio', path: '/portfolio' },
              { name: 'お知らせ', id: 'news', path: '/news' },
              { name: '会社概要', id: 'about' },
              { name: 'お問い合わせ', id: 'contact' }
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => handleNavigation(item)}
                className={`text-sm font-medium transition-all duration-300 hover:text-teal-500 hover:-translate-y-0.5 whitespace-nowrap cursor-pointer ${
                  isScrolled || !isHomePage ? 'text-gray-800 dark:text-white/90' : 'text-white/90 hover:text-white'
                }`}
              >
                {item.name}
              </button>
            ))}
            
            <div className="flex items-center gap-4 border-l border-gray-300/30 dark:border-white/20 pl-4 ml-2">
              {user && (
                <button
                  onClick={() => navigate('/admin/dashboard')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer shadow-sm ${
                    isScrolled || !isHomePage
                      ? 'bg-teal-500 text-white hover:bg-teal-600 hover:shadow-md'
                      : 'bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm'
                  }`}
                >
                  <i className="ri-admin-line"></i>
                  <span>管理画面</span>
                </button>
              )}
            </div>
          </nav>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`p-2 rounded-md transition-colors duration-300 ${
                isScrolled || !isHomePage ? 'text-gray-700 hover:bg-gray-100' : 'text-white hover:bg-white/10'
              }`}
            >
              <div className="w-6 h-6 flex items-center justify-center">
                <i className={`ri-${isMobileMenuOpen ? 'close' : 'menu'}-line text-xl`}></i>
              </div>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white/95 backdrop-blur-md rounded-lg mt-2 p-4 shadow-lg">
            <nav className="flex flex-col space-y-3">
              {[
                { name: 'ホーム', id: 'hero' },
                { name: 'サービス', id: 'services' },
                { name: 'ポートフォリオ', id: 'portfolio', path: '/portfolio' },
                { name: 'お知らせ', id: 'news', path: '/news' },
                { name: '会社概要', id: 'about' },
                { name: 'お問い合わせ', id: 'contact' }
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleNavigation(item)}
                  className="text-gray-700 hover:text-teal-600 text-left py-2 transition-colors duration-300 whitespace-nowrap cursor-pointer"
                >
                  {item.name}
                </button>
              ))}
              {user && (
                <button
                  onClick={() => {
                    navigate('/admin/dashboard');
                    setIsMobileMenuOpen(false);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors duration-300 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-admin-line"></i>
                  <span>管理画面</span>
                </button>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
